import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../index';
import { LayoutDashboard, FileSearch, Menu, X, Package, Server, Bot, MessageSquare, DollarSign, Upload, LogOut, Book, Info, FileCheck, Radio, BarChart3 } from 'lucide-react';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'chgreview', label: 'CHG Review', icon: FileSearch },
  { id: 'incidentadvisor', label: 'ITOps Advisor', icon: Bot },
  { id: 'vendorstatus', label: 'Vendor Status', icon: Radio },
  { id: 'itopsmetrics', label: 'IT Ops Metrics', icon: BarChart3 },
  { id: 'software', label: 'Software', icon: Package },
  { id: 'infrastructure', label: 'Infrastructure', icon: Server },
  { id: 'information', label: 'Information', icon: Info },
];

const adminItems = [
  { id: 'financials', label: 'Admin Dashboard', icon: DollarSign },
  { id: 'kbupload', label: 'KB Upload', icon: Upload },
  { id: 'prompts', label: 'Prompts', icon: MessageSquare },
  { id: 'reviewedchg', label: 'Reviewed CHG', icon: FileCheck },
  { id: 'kbdocs', label: 'Documentation', icon: Book },
];

const Sidebar = ({ currentPage, setCurrentPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { auth } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      auth.getUserInfo().then(info => {
        setUserInfo({ name: info.name, email: info.email });
        setIsAdmin(info.isAdmin || false);
      }).catch(() => setIsAdmin(false));
    }
  }, [auth]);

  const handleLogout = () => auth.logout();

  const NavButton = ({ item }) => {
    const isActive = currentPage === item.id;
    return (
      <button
        onClick={() => { setCurrentPage(item.id); setIsMobileMenuOpen(false); }}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          isActive
            ? 'bg-surface-2 text-text-primary border border-border'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface'
        }`}
      >
        <item.icon size={16} strokeWidth={1.8} />
        <span className="font-medium">{item.label}</span>
      </button>
    );
  };

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-lg bg-surface border border-border flex items-center justify-center text-text-secondary"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-56 bg-surface border-r border-border flex flex-col
        transform transition-transform duration-200
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="px-4 py-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <img src="/vision-logo.png" alt="VISION" className="h-7 w-7 object-contain" />
            <span className="text-base font-semibold text-text-primary tracking-wide">VISION</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          <div className="space-y-0.5">
            {navItems.map(item => <NavButton key={item.id} item={item} />)}
          </div>

          {isAdmin && (
            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-[11px] font-medium text-text-muted uppercase tracking-wider px-3 mb-2">Admin</p>
              <div className="space-y-0.5">
                {adminItems.map(item => <NavButton key={item.id} item={item} />)}
              </div>
            </div>
          )}
        </nav>

        {/* User */}
        <div className="px-3 py-3 border-t border-border">
          <div className="flex items-center gap-2.5 px-2 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-surface-2 border border-border flex items-center justify-center text-text-secondary text-xs font-semibold shrink-0">
              {userInfo.name ? userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{userInfo.name || 'User'}</p>
              <p className="text-xs text-text-muted truncate">{userInfo.email || ''}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-secondary hover:text-negative hover:bg-negative/10 transition-colors"
          >
            <LogOut size={14} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
